import AnalyticsChart from "@/components/artist-interface/AnalyticsChart";
import Catalog from "@/components/artist-interface/Catalog";
import StatHeader from "@/components/artist-interface/StatHeader";
import VideoCarousel from "@/components/artist-interface/VideoCarousel";
import LoginModal from "@/components/modals/LoginModal";
import { SessionContext } from "@/context/SessionContext";
import { ActionIcon, Box, Button, Card, Group, Stack, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconLock, IconQuestionMark } from "@tabler/icons-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

const HIGHLIGHT_DURATION = 800; // ms

const ArtistDashboard: React.FC = () => {
	const theme = useMantineTheme();
	const session = useContext(SessionContext)
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const [artistName, setArtistName] = useState<String>("");
	const { artistId } = useParams();

	const [userHasAccess, setUserHasAccess] = useState<boolean>(false);
	const [showLoginModal, setShowLoginModal] = useState<boolean>(true);

	const [headerOpacity, setHeaderOpacity] = useState(1);

	const dashboardRef = useRef<HTMLDivElement>(null);
	const analyticsRef = useRef<HTMLDivElement>(null);
	const videosRef = useRef<HTMLDivElement>(null);
	const catalogRef = useRef<HTMLDivElement>(null);

	const [highlightedSection, setHighlightedSection] = useState<string | null>(null);

	if (!session) {
		return null
	}

	const handleNavClick = (ref: React.RefObject<HTMLDivElement>, section: string) => {
		ref.current?.scrollIntoView({ behavior: "smooth" });
		setHighlightedSection(section);
	};

	useEffect(() => {
		if (artistId) {
			session.fetchArtistStats(artistId);
		}
	}, [artistId]);

	useEffect(() => {
		if (highlightedSection) {
			const timeout = setTimeout(() => setHighlightedSection(null), HIGHLIGHT_DURATION);
			return () => clearTimeout(timeout);
		}
	}, [highlightedSection]);

	useEffect(() => {
		const handleScroll = () => {
			const scrollY = window.scrollY;
			let newOpacity = 1 - Math.min(scrollY / 200, 0.7); // Minimum opacity 0.3
			if (newOpacity < 0.3) newOpacity = 0.3;
			setHeaderOpacity(newOpacity);
		};
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		if (session.user?.artists) {
			for (let i = 0; i < session.user?.artists.length; i++) {
				if (session.user.artists[i].artistId == artistId) {
					setArtistName(session.user.artists[i].artistName)
				}
			}
		}
	}, [session, artistId]);

	const handleMouseEnter = () => setHeaderOpacity(1);

	const handleMouseLeave = () => {
		const scrollY = window.scrollY;
		let newOpacity = 1 - Math.min(scrollY / 200, 0.7);
		if (newOpacity < 0.3) newOpacity = 0.3;
		setHeaderOpacity(newOpacity);
	};

	const getHighlightBoxShadow = (section: string): React.CSSProperties =>
		highlightedSection === section
			? {
				boxShadow: `0 0 10px 5px ${theme.colors.blue[1]}`,
				transition: "box-shadow 0.2s",
				zIndex: 10,
				position: "relative",
			}
			: {
				transition: "box-shadow 0.4s",
			};
			
	useEffect(() => {
		if (session.user && session.user.artists.some(artist => artist.artistId === artistId)) {
			setUserHasAccess(true);
			setShowLoginModal(false);
		} else {
			setUserHasAccess(false);
			if (session.user == null) {
				setShowLoginModal(true);
			}
		}
	}, [session.user, artistId]);

	if (!userHasAccess) {
		return (
			<>
				<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
				<Card p={20}>
					<IconLock size={48} color={theme.colors.red[6]} />
					<Text>You do not have permission to access this page.</Text>
					<Text>Please log into an account with access or <Link to="/">click here to navigate away</Link>.</Text>
				</Card>
			</>
		);
	}

	const baseSectionStyle: React.CSSProperties = {
		flex: 1,
		background: theme.colors.blue[8],
		borderRadius: "20px",
		boxShadow: "0px 0px 10px 1px #334F73",
		scrollMarginTop: "100px",
		transition: "box-shadow 0.4s",
	};

	return (
		<Group ref={dashboardRef} align="flex-start" style={{ minHeight: "100vh" }}>
			<Stack align="center" justify="center" w="100%">
				{/* Header */}
				<Group
					align="center"
					justify="center"
					m={20}
					w={isMobile ? "80%" : "50%"}
					bg={theme.colors.blue[8]}
					style={{
						borderRadius: "20px",
						boxShadow: "0px 0px 10px 1px #334F73"
					}}
					wrap="nowrap"
				>
					<Stack
						w="100%"
						maw={600}
						mx="auto"
						align="center"
						justify="center"
					>
						<Text
							p={20}
							pb={0}
							fz={isMobile ? 20 : 45}
							c={theme.colors.blue[0]}
							ta="center"
							style={{
								minWidth: "300px",
							}}
						>
							My Artist
						</Text>
						<Text
							mb={5}
							fz={isMobile ? 10 : 20}
							c={theme.colors.blue[0]}
							ta="center"
							style={{
								minWidth: "300px",
							}}
						>
							{artistName}
						</Text>
					</Stack>
				</Group>
				{/* Sticky Navigation */}
				<Box
					pos="sticky"
					top="50px"
					w="100%"
					display="flex"
					style={{
						zIndex: 100,
						justifyContent: "center",

					}}
				>
					<Group
						align="center"
						justify="center"
						mt={10}
						mb={25}
						p={1}
						bg={theme.colors.blue[0]}
						w={isMobile ? "90%" : "30%"}
						style={{
							transition: "background-color 0.2s, opacity 0.2s",
							opacity: headerOpacity,
							borderRadius: "10px",
						}}
						wrap="nowrap"
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						<Group m={0} p={0}>
							<Button
								bg={theme.colors.blue[0]}
								c={theme.colors.blue[8]}
								onClick={() => handleNavClick(videosRef, "performanceVideos")}
							>
								<Text fw={700} fz={isMobile ? 11 : 14}>
									Performance Videos
								</Text>
							</Button>
							<Button
								bg={theme.colors.blue[0]}
								c={theme.colors.blue[8]}
								onClick={() => handleNavClick(analyticsRef, "analytics")}
							>
								<Text fw={700} fz={isMobile ? 11 : 14}>
									Analytics
								</Text>
							</Button>
							<Button
								bg={theme.colors.blue[0]}
								c={theme.colors.blue[8]}
								onClick={() => handleNavClick(catalogRef, "catalog")}
							>
								<Text fw={700} fz={isMobile ? 11 : 14}>
									My Catalog
								</Text>
							</Button>
						</Group>
					</Group>
				</Box>
				<Box>
					<StatHeader />
				</Box>
				<Box
					w="90%"
					mx="auto"
					m={40}
					mb={100}
					style={{
						display: "flex",
						flexDirection: "row",
						alignItems: "flex-start",
						flexWrap: "wrap",
						gap: "2rem"
					}}
				>
					{/* Performance Videos Section */}
					<Box
						ref={videosRef}
						w={"50%"}
						h={isMobile ? 500 : 700}
						data-section="Performance Videos"
						style={{
							...baseSectionStyle,
							...getHighlightBoxShadow("performanceVideos")
						}}
					>
						<Group
							align="center"
							justify="center"
							mt={20}
							ml={10}
							bg={theme.colors.blue[0]}
							w={isMobile ? "60%" : "30%"}
							style={{
								borderRadius: "20px",
								margin: "0 auto"
							}}
							wrap="nowrap"
						>
							<Text
								p={5}
								fz={17}
								c={theme.colors.blue[8]}
								ta="center"
								w="100%"
							>
								Performance Videos
							</Text>
						</Group>
						<Box p="md">
							<VideoCarousel />
						</Box>
					</Box>
					{/* Analytics Section */}
					<Box
						ref={analyticsRef}
						w={"50%"}
						h={isMobile ? 500 : 700}
						style={{
							...baseSectionStyle,
							...getHighlightBoxShadow("analytics")
						}}
					>
						<Box
							bg={theme.colors.blue[0]}
							w={isMobile ? "60%" : "30%"}
							mt={20}
							ml={10}
							style={{
								borderRadius: "20px",
								margin: "0 auto",
								position: "relative",
								minHeight: 40,
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<Text
								p={5}
								fz={17}
								c={theme.colors.blue[8]}
								ta="center"
								w="100%"
								style={{
									width: "100%",
									textAlign: "center",
									zIndex: 1,
									pointerEvents: "none"
								}}
							>
								Analytics
							</Text>
							<Tooltip
								label={
									<>
										Measures Submitted: Measures used in a project submission.<br />
										Measures Dragged: Measures dragged into a project.<br />
										Previews: Times a song's stem is played in the library.<br />
										Performances: Performances containing a piece of your song.<br />
									</>
								}
								withArrow
								position="right"
								openDelay={200}
							>
								<ActionIcon
									variant="subtle"
									size={24}
									color={theme.colors.blue[8]}
									style={{
										position: "absolute",
										right: 10,
										top: "50%",
										transform: "translateY(-50%)"
									}}
									aria-label="Analytics Info"
								>
									<IconQuestionMark size={18} color={theme.colors.blue[8]} />
								</ActionIcon>
							</Tooltip>
						</Box>
						<Box p="md">
							<AnalyticsChart />
						</Box>
					</Box>
				</Box>
				{/* Catalog Section - full width below */}
				<Stack
					mb={100}
					ref={catalogRef}
					w="90%"
					mx="auto"
					style={{
						...getHighlightBoxShadow("catalog")
					}}
					bg={theme.colors.blue[8]}
				>
					<Box
						bg={theme.colors.blue[0]}
						w={isMobile ? "60%" : "15%"}
						mt={20}
						ml={10}
						style={{
							borderRadius: "20px",
							margin: "0 auto",
							position: "relative",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 40,
						}}
					>
						<Text
							p={5}
							fz={17}
							c={theme.colors.blue[8]}
							ta="center"
							w="100%"
							style={{
								width: "100%",
								textAlign: "center",
								zIndex: 1,
								pointerEvents: "none"
							}}
						>
							My Catalog
						</Text>
						<Tooltip
							label={
								<>
									Measures Submitted: Measures used in a project submission.<br />
									Measures Dragged: Measures dragged into a project.<br />
									Previews: Times a song's stem is played in the library.<br />
									Performances: Performances containing a piece of your song.<br />
								</>
							}
							withArrow
							position="right"
							openDelay={200}
						>
							<ActionIcon
								variant="subtle"
								size={24}
								color={theme.colors.blue[8]}
								style={{
									position: "absolute",
									right: 8,
									top: "50%",
									transform: "translateY(-50%)",
								}}
								aria-label="Analytics Info"
							>
								<IconQuestionMark size={18} color={theme.colors.blue[8]} />
							</ActionIcon>
						</Tooltip>
					</Box>
					<Group align="center" justify="center" w="100%">
						<Box w={"75%"} mb={30}>
							<Catalog />
						</Box>
					</Group>
				</Stack>
			</Stack>
		</Group>
	);
};

export default ArtistDashboard;